/**
 * Passport hub feed helpers — date grouping, soft vehicle batching, milestones.
 * No XP/points language. Uses real achievement thresholds from the catalog.
 */

import type { PassportEncounter, PassportSubjectType } from '../../services/api/passport';

export type PassportFeedFilter =
  | 'all'
  | 'vehicle'
  | 'event'
  | 'place'
  | 'checkpoint'
  | 'other';

export type PassportFeedItem =
  | { kind: 'encounter'; encounter: PassportEncounter }
  | {
      kind: 'vehicle_batch';
      dayKey: string;
      encounters: PassportEncounter[];
      title: string;
    };

export type PassportFeedSection = {
  key: string;
  label: string;
  items: PassportFeedItem[];
};

/** Vehicle-count milestones that map to issued Passport achievements. */
export const PASSPORT_VEHICLE_MILESTONES = [10, 50, 100] as const;

export function parseEncounterDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function encounterDayKey(value: string | null | undefined): string {
  const d = parseEncounterDate(value);
  if (!d) return 'unknown';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function passportFeedSectionLabel(dayKey: string, now = new Date()): string {
  if (dayKey === 'unknown') return 'Earlier';
  const [y, m, day] = dayKey.split('-').map(Number);
  const date = new Date(y, m - 1, day);
  const today = startOfDay(now);
  const diffDays = Math.round((today.getTime() - startOfDay(date).getTime()) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays > 1 && diffDays < 7) return 'This week';
  return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
}

export function matchesPassportFeedFilter(
  encounter: PassportEncounter,
  filter: PassportFeedFilter
): boolean {
  if (filter === 'all') return true;
  const t = encounter.subject_type;
  if (filter === 'vehicle') return t === 'vehicle';
  if (filter === 'event') return t === 'event';
  if (filter === 'place') return t === 'shop' || t === 'destination' || t === 'club';
  if (filter === 'checkpoint') {
    return t === 'checkpoint' || t === 'booth' || t === 'activation';
  }
  return !['vehicle', 'event', 'shop', 'destination', 'club', 'checkpoint', 'booth', 'activation'].includes(
    t
  );
}

/**
 * Soft-group consecutive vehicle discoveries on the same calendar day (3+).
 * Preserves chronological order; individual records remain reachable via expand links.
 */
export function buildPassportFeedItems(
  encounters: PassportEncounter[],
  opts: { batchVehicles?: boolean } = {}
): PassportFeedItem[] {
  const batchVehicles = opts.batchVehicles !== false;
  const items: PassportFeedItem[] = [];
  let i = 0;
  while (i < encounters.length) {
    const current = encounters[i];
    if (!batchVehicles || current.subject_type !== 'vehicle') {
      items.push({ kind: 'encounter', encounter: current });
      i += 1;
      continue;
    }
    const day = encounterDayKey(current.occurred_at);
    let j = i;
    while (
      j < encounters.length &&
      encounters[j].subject_type === 'vehicle' &&
      encounterDayKey(encounters[j].occurred_at) === day
    ) {
      j += 1;
    }
    const slice = encounters.slice(i, j);
    if (slice.length >= 3) {
      items.push({
        kind: 'vehicle_batch',
        dayKey: day,
        encounters: slice,
        title: `You discovered ${slice.length} builds`,
      });
    } else {
      for (const enc of slice) {
        items.push({ kind: 'encounter', encounter: enc });
      }
    }
    i = j;
  }
  return items;
}

export function groupPassportFeedSections(
  encounters: PassportEncounter[],
  opts?: { batchVehicles?: boolean; now?: Date }
): PassportFeedSection[] {
  const items = buildPassportFeedItems(encounters, { batchVehicles: opts?.batchVehicles });
  const now = opts?.now ?? new Date();
  const sections: PassportFeedSection[] = [];
  const byLabel = new Map<string, PassportFeedSection>();

  for (const item of items) {
    const dayKey =
      item.kind === 'vehicle_batch' ? item.dayKey : encounterDayKey(item.encounter.occurred_at);
    const label = passportFeedSectionLabel(dayKey, now);
    let section = byLabel.get(label);
    if (!section) {
      section = { key: label, label, items: [] };
      byLabel.set(label, section);
      sections.push(section);
    }
    section.items.push(item);
  }
  return sections;
}

export type PassportVehicleMilestone = {
  current: number;
  next: number;
  remaining: number;
  label: string;
};

export function nextVehicleMilestone(vehicleCount: number): PassportVehicleMilestone | null {
  const current = Math.max(0, Math.floor(vehicleCount));
  for (const next of PASSPORT_VEHICLE_MILESTONES) {
    if (current < next) {
      return {
        current,
        next,
        remaining: next - current,
        label:
          next === 10
            ? '10 builds discovered'
            : next === 50
              ? '50 builds discovered'
              : '100 builds discovered',
      };
    }
  }
  return null;
}

export function encounterDisplayTitle(encounter: PassportEncounter): string {
  return encounter.subject?.title?.trim() || subjectTypeFallback(encounter.subject_type);
}

export function subjectTypeFallback(type: PassportSubjectType): string {
  switch (type) {
    case 'vehicle':
      return 'Build';
    case 'event':
      return 'Event';
    case 'shop':
      return 'Shop';
    case 'club':
      return 'Club';
    case 'checkpoint':
      return 'Checkpoint';
    case 'booth':
      return 'Booth';
    case 'activation':
      return 'Activation';
    case 'destination':
      return 'Place';
    default:
      return 'Encounter';
  }
}

export function isSignificantEncounter(
  encounter: PassportEncounter,
  indexInVerifiedVehicles: number
): boolean {
  if (encounter.subject_type !== 'vehicle' || encounter.status !== 'verified') return false;
  const n = indexInVerifiedVehicles + 1;
  if (n === 1 || n === 10 || n === 25 || n === 50 || n === 100) return true;
  const stars = encounter.subject?.rarity_stars;
  if (typeof stars === 'number' && stars >= 4) return true;
  const collectibleStars = encounter.subject?.collectible?.rarity_stars;
  if (typeof collectibleStars === 'number' && collectibleStars >= 4) return true;
  return false;
}
